import "./AppLayout.css";
import { ButtonIcon, MenuList, PageBoundary } from "@fpsoftware/mvp";
import cn from "classnames";
import {
  type ComponentPropsWithRef,
  type FC,
  useEffect,
  useState,
} from "react";
import {
  TbBug,
  TbCreditCard,
  TbHomeDollar,
  TbLogout2,
  TbMenuDeep,
  TbSettings,
  TbShoppingCart,
  TbWorldPlus,
} from "react-icons/tb";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { useProfile } from "@/auth/useProfile";
import { useHelpActions } from "@/help/useHelpActions";
import { LinkGen } from "@/LinkGen";

export const AppLayout = () => {
  const { logout } = useAuth();
  const { profile } = useProfile();
  const { reportBug, capturing } = useHelpActions();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Actually needed because it's a side effect
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <div className={cn("app-layout", { open: menuOpen })}>
      <aside>
        <div>
          <img
            className="logo"
            src={`data:;base64,${profile.companyLogo}`}
            alt={profile.email}
          />
          <MenuList className="menu">
            <MenuList.Link
              to={LinkGen.RFQ()}
              icon={TbHomeDollar}
              disabled={!profile.subscription}
            >
              RFQs
            </MenuList.Link>
            <MenuList.Link
              to={LinkGen.Purchase()}
              icon={TbShoppingCart}
              disabled={!profile.subscription}
            >
              Purchases
            </MenuList.Link>
          </MenuList>
        </div>

        <div>
          <div>Account</div>
          <MenuList className="menu">
            <MenuList.Link to={LinkGen.Subscription()} icon={TbCreditCard}>
              Subscription
            </MenuList.Link>
            <MenuList.Link
              to={LinkGen.Settings()}
              icon={TbSettings}
              disabled={!profile.subscription}
            >
              Settings
            </MenuList.Link>
            <MenuList.Link
              to={LinkGen.Integration()}
              icon={TbWorldPlus}
              disabled={!profile.subscription}
            >
              Website integration
            </MenuList.Link>
            <MenuList.Button
              icon={TbBug}
              onClick={reportBug}
              disabled={capturing}
            >
              {capturing ? "Capturing…" : "Report a bug"}
            </MenuList.Button>
            <MenuList.Button icon={TbLogout2} onClick={() => logout.mutate()}>
              Logout
            </MenuList.Button>
          </MenuList>
        </div>
      </aside>
      <section>
        <div className="header">
          <span>&nbsp;</span>
          <ButtonIcon
            className="hamburger"
            icon={TbMenuDeep}
            onClick={() => setMenuOpen((x) => !x)}
          />
        </div>
        <PageBoundary>
          <Outlet />
        </PageBoundary>
      </section>
      <button
        type="button"
        className="backdrop"
        onClick={() => setMenuOpen(false)}
      />
    </div>
  );
};

interface PageProps extends ComponentPropsWithRef<"section"> {
  title: string;
}

export const AppPage: FC<PageProps> = (props) => {
  const { title, className, ...rest } = props;
  return (
    <>
      <title>{title}</title>
      <section className={cn("app-page", className)} {...rest} />
    </>
  );
};
